import * as Sentry from "@sentry/react";
import { MediaError } from "../../errors/sentryErrors";

const formatDateForPDF = (dateStr) => {
  if (!dateStr) return "";
  const isoMatch = dateStr.match(/(\d{4})-(\d{2})-(\d{2})/);
  if (isoMatch) {
    return `${isoMatch[3]}-${isoMatch[2]}-${isoMatch[1]}`;
  }
  return dateStr;
};

const formatCurrency = (amount, currency = 'INR') => {
  if (!amount && amount !== 0) return '';
  const num = typeof amount === 'string' ? parseFloat(amount.replace(/[^0-9.-]/g, '')) : amount;
  if (isNaN(num)) return amount;

  const symbols = {
    'INR': 'Rs.',
    'USD': '$',
    'EUR': 'EUR',
    'GBP': 'GBP',
    'THB': 'THB',
    'SGD': 'SGD',
    'AED': 'AED',
    'JPY': 'JPY'
  };
  const symbol = symbols[currency] || currency;
  return `${symbol} ${num.toLocaleString('en-IN')}`;
};

const parseCostString = (costStr) => {
  if (!costStr) return '';
  const cleaned = String(costStr).replace(/[^\d,.-]/g, '').replace(/,/g, '');
  const num = parseFloat(cleaned);
  if (isNaN(num)) return costStr;
  return num;
};

const formatCategoryName = (name) => {
  if (!name) return '';
  const spaced = name.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());
  return spaced.trim();
};

const safeString = (value) => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    if (value.min !== undefined && value.max !== undefined) return `${value.min}-${value.max}`;
    if (value.average !== undefined) return String(value.average);
    if (value.value !== undefined) return String(value.value);
    return '';
  }
  return String(value);
};

const loadImageAsBase64 = (url) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      const dataURL = canvas.toDataURL('image/png');
      resolve(dataURL);
    };
    img.onerror = () => reject(new Error('Failed to load image'));
    img.src = url;
  });
};

export const generateItineraryPDF = async (itinerary, tripData, finalCostSummary, checkedPackingItems = {}, fetchedHotels = null) => {
  const { jsPDF } = await import('jspdf');
  const { tripSummary, accommodationDetails, dayWiseItinerary, importantNotes, weatherAnalysis, packingChecklist, inclusions, exclusions } = itinerary;

  const doc = new jsPDF('p', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  const contentWidth = pageWidth - (margin * 2);
  let y = 0;

  const teal = [13, 148, 136];
  const tealDark = [15, 118, 110];
  const tealLight = [240, 253, 250];
  const gray = [107, 114, 128];
  const darkGray = [55, 65, 81];
  const lightGray = [243, 244, 246];
  const white = [255, 255, 255];
  const amber = [217, 119, 6];
  const amberLight = [254, 243, 199];

  const currency = tripSummary?.budget?.currency || tripData?.currency || 'INR';

  const userEnteredBudget = tripData?.budget && tripData.budget !== "" && Number(tripData.budget) > 0;
  const userBudgetAmount = userEnteredBudget ? Number(tripData.budget) : null;

  const sanitizeText = (str) => {
    if (!str) return '';
    return String(str)
      .replace(/[\u2018\u2019]/g, "'")
      .replace(/[\u201C\u201D]/g, '"')
      .replace(/[\u2013\u2014]/g, '-')
      .replace(/[\u2026]/g, '...')
      .replace(/[\u2190-\u2199\u279C]/g, '->')
      .replace(/[^\x20-\x7E\r\n]/g, ''); 
  };

  const formatSafeDate = (dateVal, options) => {
    if (!dateVal) return '';
    const d = new Date(dateVal);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleDateString('en-GB', options);
  };

  const splitText = (text, maxWidth) => {
    return doc.splitTextToSize(sanitizeText(text), maxWidth);
  };

  const checkPageBreak = (neededHeight) => {
    if (y + neededHeight > pageHeight - 25) {
      doc.addPage();
      y = 20;
      return true;
    }
    return false;
  };

  const drawSectionHeader = (title, iconText = '') => {
    doc.setFillColor(...teal);
    doc.roundedRect(margin, y, contentWidth, 12, 3, 3, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text(title, margin + 6, y + 8);
    y += 18;
  };

  // --- COVER PAGE (PAGE 1) ---
  let coverBase64 = null;
  if (itinerary?.destinationImages && itinerary.destinationImages.length > 0) {
    try {
      // destinationImages might be an array of strings or array of objects with a .url property
      const imgObj = itinerary.destinationImages[0];
      const imgUrl = typeof imgObj === 'string' ? imgObj : imgObj.url;
      if (imgUrl) {
        coverBase64 = await loadImageAsBase64(imgUrl);
      }
    } catch (e) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "media");
        scope.setTag("app", "web");
        scope.setTag("error_source", "browser");
        Sentry.captureException(new MediaError(e instanceof Error ? e.message : String(e)));
      });
      console.warn("Could not load cover image", e);
    }
  }

  if (coverBase64) {
    // Fill background with image
    doc.addImage(coverBase64, 'JPEG', 0, 0, pageWidth, pageHeight);
    
    // Add a dark overlay to make text readable (using semi-transparent dark rectangle)
    if (typeof doc.setGState === 'function') {
      doc.setGState(new doc.GState({ opacity: 0.4 }));
      doc.setFillColor(0, 0, 0);
      doc.rect(0, 0, pageWidth, pageHeight, 'F');
      doc.setGState(new doc.GState({ opacity: 1.0 }));
    }
  } else {
    // Fallback solid color
    doc.setFillColor(30, 41, 59); // slate-800
    doc.rect(0, 0, pageWidth, pageHeight, 'F');
  }

  // Draw Logo banner at top center
  doc.setFillColor(255, 255, 255);
  doc.rect((pageWidth - 80) / 2, 10, 80, 25, 'F');
  try {
    const logoBase64 = await loadImageAsBase64('/RoamMyWay.png');
    doc.addImage(logoBase64, 'PNG', (pageWidth - 60) / 2, 13, 60, 19);
  } catch (e) {
    Sentry.withScope((scope) => {
      scope.setTag("feature", "media");
      scope.setTag("app", "web");
      scope.setTag("error_source", "browser");
      Sentry.captureException(new MediaError(e instanceof Error ? e.message : String(e)));
    });
    doc.setTextColor(...tealDark);
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.text('RoamMyWay', pageWidth / 2, 25, { align: 'center' });
  }

  // Destination Text
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(18);
  doc.setFont('helvetica', 'normal');
  doc.text('Travel with RoamMyWay', pageWidth / 2, pageHeight / 3 - 10, { align: 'center' });

  let destTitle = 'DESTINATION';
  if (tripData?.destinations && tripData.destinations.length > 0) {
    destTitle = tripData.destinations[0].name || tripData.destinations[0].city || 'DESTINATION';
  } else if (tripSummary?.destination) {
    // Fallback if tripData.destinations is missing, split by any separator including em-dashes
    destTitle = tripSummary.destination.split(/[,|\-—–&]/)[0];
  }
  destTitle = destTitle.toUpperCase().trim();
  
  doc.setFontSize(75);
  doc.setFont('helvetica', 'bold');
  // If the title is too long, shrink the font size
  let titleWidth = doc.getTextWidth(destTitle);
  let fontSize = 75;
  while (titleWidth > pageWidth - 20 && fontSize > 30) {
    fontSize -= 5;
    doc.setFontSize(fontSize);
    titleWidth = doc.getTextWidth(destTitle);
  }
  doc.text(destTitle, pageWidth / 2, (pageHeight / 3) + 20, { align: 'center' });

  // Bottom Information Panel (White Box)
  const bottomBoxHeight = 45;
  const bottomBoxY = pageHeight - bottomBoxHeight - 15;
  doc.setFillColor(255, 255, 255);
  doc.roundedRect(10, bottomBoxY, pageWidth - 20, bottomBoxHeight, 3, 3, 'F');

  // Left side: Trip Details
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  const tripIdStr = tripData?.tripId || tripData?._id?.substring(0, 8) || '3391223';
  doc.text(`Trip ID: #${tripIdStr}`, 15, bottomBoxY + 10);
  
  doc.setTextColor(50, 50, 50);
  doc.setFont('helvetica', 'bold');
  
  const customerName = tripData?.customerName || 'Customer';
  
  // Calculate PAX breakdown string
  const paxBreakdown = tripData?.rooms ? tripData.rooms.reduce((acc, r) => {
    acc.adults += r.adults || 0;
    acc.children += r.children || 0;
    return acc;
  }, { adults: 0, children: 0 }) : { adults: tripData?.adults || 1, children: tripData?.children || 0 };
  
  let paxStr = `${paxBreakdown.adults}A`;
  if (paxBreakdown.children > 0) {
    paxStr += `, ${paxBreakdown.children}C`;
  }
  doc.text(`${customerName} • ${paxStr}`, 15, bottomBoxY + 16);

  // Timeline graphic
  doc.setDrawColor(200, 200, 200);
  doc.setLineWidth(0.5);
  doc.line(18, bottomBoxY + 23, pageWidth / 2, bottomBoxY + 23);
  
  // Try to use a square or a simple icon instead of emoji if font doesn't support it
  doc.setFillColor(217, 119, 6); // amber
  doc.rect(15, bottomBoxY + 21, 3, 4, 'F');
  doc.setFillColor(16, 185, 129); // emerald
  doc.rect(pageWidth / 2 - 3, bottomBoxY + 21, 3, 4, 'F');

  // Dates
  const startDateStr = tripData?.startDate ? new Date(tripData.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'TBD';
  const endDateStr = tripData?.endDate ? new Date(tripData.endDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'TBD';
  const durationStr = tripSummary?.duration || '';

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 30, 30);
  doc.text(`Starts: ${startDateStr}`, 15, bottomBoxY + 33);
  doc.text(`Ends: ${endDateStr} (${durationStr})`, pageWidth / 2 + 2, bottomBoxY + 33, { align: 'right' });

  // Separator line
  doc.setDrawColor(230, 230, 230);
  doc.line(pageWidth / 2 + 10, bottomBoxY + 5, pageWidth / 2 + 10, bottomBoxY + bottomBoxHeight - 5);

  // Right side: Holiday Expert
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Holiday Expert', pageWidth / 2 + 20, bottomBoxY + 12);
  
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('RoamMyWay', pageWidth / 2 + 20, bottomBoxY + 22);
  doc.setFontSize(10);
  doc.text('+91-8949491119', pageWidth / 2 + 20, bottomBoxY + 28);

  // Footer bar
  doc.setFillColor(30, 30, 30);
  doc.rect(0, pageHeight - 15, pageWidth, 15, 'F');
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Tel: +91 63762 00926 (Vidhan Mehta)', 15, pageHeight - 9);
  doc.text('Tel: +91 63759 13360 (Manan Mehta)', 15, pageHeight - 4);
  doc.text('Email: reservation@roammyway.in', pageWidth - 15, pageHeight - 6.5, { align: 'right' });

  // Add new page for Confirmed Quote
  doc.addPage();
  
  // 1. Confirmed Quote Badge
  const quoteBadgeWidth = 60;
  const quoteBadgeX = (pageWidth - quoteBadgeWidth) / 2;
  doc.setFillColor(167, 243, 208); // Light green background (tailwind emerald-200)
  doc.setDrawColor(5, 150, 105); // Dark green border (tailwind emerald-600)
  doc.setLineWidth(0.5);
  doc.rect(quoteBadgeX, 15, quoteBadgeWidth, 10, 'FD');
  doc.setTextColor(5, 150, 105);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('CONFIRMED QUOTE', pageWidth / 2, 21.5, { align: 'center' });
  
  let qy = 35;
  
  // 2. Greeting Section
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(10);
  doc.text(`Dear Mr. ${customerName} ji,`, margin, qy);
  qy += 8;
  doc.setFont('helvetica', 'normal');
  doc.text('Greetings from RoamMyWay.', margin, qy);
  qy += 8;
  const quoteMsg = "Our sales team has put up this Quote regarding your upcoming trip. Please go through it and let's know if you would like any changes in any of the provided services. Contact details are provided at the end.";
  const quoteMsgLines = splitText(quoteMsg, contentWidth);
  doc.text(quoteMsgLines, margin, qy);
  qy += quoteMsgLines.length * 5 + 5;
  
  // 3. Divider
  doc.setDrawColor(230, 230, 230);
  doc.line(margin, qy, pageWidth - margin, qy);
  qy += 6;
  
  // 4. Trip Details Grid
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(150, 150, 150);
  
  const col1X = margin;
  const col2X = margin + 80;
  const col3X = margin + 130;
  
  doc.text('DESTINATION', col1X, qy);
  doc.text('START DATE', col2X, qy);
  doc.text('DURATION', col3X, qy);
  
  qy += 6;
  
  doc.setFontSize(11);
  doc.setTextColor(30, 30, 30);
  const qDestTitle = (tripData?.destinations && tripData.destinations[0]?.name) || destTitle;
  doc.text(qDestTitle, col1X, qy);
  const startDateStrFull = tripData?.startDate ? new Date(tripData.startDate).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' }) : 'TBD';
  doc.text(startDateStrFull, col2X, qy);
  const qDuration = tripSummary?.duration || `${tripData?.rooms?.[0]?.nights || 0} Nights / ${tripData?.rooms?.[0]?.nights ? tripData.rooms[0].nights + 1 : 1} Days`;
  doc.text(qDuration, col3X, qy);
  
  qy += 10;
  
  doc.setFontSize(8);
  doc.setTextColor(150, 150, 150);
  doc.text('PAX', col1X, qy);
  doc.text('TRIP ID', col2X, qy);
  
  qy += 6;
  
  doc.setFontSize(11);
  doc.setTextColor(30, 30, 30);
  doc.text(`${paxBreakdown.adults} Adults${paxBreakdown.children > 0 ? `, ${paxBreakdown.children} Children` : ''}`, col1X, qy);
  doc.text(String(tripIdStr).substring(0, 8), col2X, qy);
  
  qy += 15;
  
  // 5. Quote Price Section
  // Banner
  doc.setFillColor(191, 219, 254); // Light blue (tailwind blue-200)
  doc.rect(margin, qy, contentWidth, 12, 'F');
  doc.setFillColor(37, 99, 235); // Dark blue left border (tailwind blue-600)
  doc.rect(margin, qy, 3, 12, 'F');
  doc.setTextColor(37, 99, 235);
  doc.setFontSize(12);
  doc.text('Quote Price', margin + 8, qy + 8);
  
  qy += 12;
  
  // Table outline
  const tableHeight = 22;
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.3);
  doc.rect(margin, qy, contentWidth, tableHeight);
  // Horizontal divider
  doc.line(margin, qy + 8, pageWidth - margin, qy + 8);
  // Vertical divider
  const midX = margin + contentWidth / 2;
  doc.line(midX, qy, midX, qy + tableHeight);
  
  // Headers
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(9);
  doc.text('Total (INR)', margin + 3, qy + 6);
  doc.text('Per Pax (INR)', midX + 3, qy + 6);
  
  // Calculate Prices
  let totalCostNum = 0;
  if (finalCostSummary?.estimatedTotal) {
    totalCostNum = parseCostString(finalCostSummary.estimatedTotal);
  }
  const perPaxNum = paxBreakdown.adults > 0 ? Math.round(totalCostNum / paxBreakdown.adults) : totalCostNum;
  
  const formatPrice = (num) => num ? num.toLocaleString('en-IN') : '0';
  
  qy += 15;
  
  // Total Price
  doc.setFontSize(14);
  doc.text(`${formatPrice(totalCostNum)} /-`, margin + 3, qy);
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(100, 100, 100);
  doc.text('(including GST)', margin + 3, qy + 4);
  
  // Per Pax Price
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(30, 30, 30);
  doc.text(`${formatPrice(perPaxNum)} /-`, midX + 3, qy);
  
  doc.setFontSize(9);
  doc.text(`Per Person (Double Sharing) x ${paxBreakdown.adults} Pax`, midX + 3 + doc.getTextWidth(`${formatPrice(perPaxNum)} /-`) + 2, qy);
  
  // Add new page for actual content (Cities Breakdown, etc.)
  doc.addPage();
  y = 20;

  // --- Page 3: Hotels / Accommodations ---
  const getOrdinal = (n) => {
    const s = ["th", "st", "nd", "rd"];
    const v = n % 100;
    return n + (s[(v - 20) % 10] || s[v] || s[0]);
  };

  if (accommodationDetails && accommodationDetails.length > 0) {
    // Header
    doc.setFillColor(191, 219, 254); // Light blue banner
    doc.rect(margin, y, contentWidth, 12, 'F');
    doc.setFillColor(37, 99, 235); // Dark blue left border
    doc.rect(margin, y, 4, 12, 'F');
    doc.setTextColor(37, 99, 235);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Hotels / Accommodations', margin + 10, y + 8);
    y += 20;

    let currentNightIdx = 1;
    for (let hIdx = 0; hIdx < accommodationDetails.length; hIdx++) {
      const hotel = accommodationDetails[hIdx];
      checkPageBreak(50);
      const nightsNum = parseInt(hotel.nights) || 1;

      let hotelCity = hotel.city;
      if (!hotelCity && tripSummary?.citiesBreakdown?.length > 0) {
        const actualCities = tripSummary.citiesBreakdown.filter(c => c.type !== 'departure');
        if (actualCities[hIdx]) {
          hotelCity = actualCities[hIdx].city;
        }
      }
      if (!hotelCity) {
        const nameStr = hotel.propertyName || hotel.hotelName || hotel.name || '';
        const match = nameStr.match(/in ([a-zA-Z\s]+)$/i);
        if (match) hotelCity = match[1].trim();
      }
      if (!hotelCity) {
        hotelCity = tripData?.destinations?.[0]?.name || destTitle;
      }

      const checkInDate = tripData?.startDate ? new Date(tripData.startDate) : new Date();
      checkInDate.setDate(checkInDate.getDate() + currentNightIdx - 1);
      const checkInStr = checkInDate.toLocaleDateString('en-GB', { day: 'numeric', month: 'long' });

      // Night Badges calculation first for layout
      let badgeX = margin + 5;
      const badgeY = y + 5;
      
      const badgeText = nightsNum === 1 ? '1 Night' : `${nightsNum} Nights`;
      
      // Hotel Name lines
      const realHotelsForCity = fetchedHotels?.[hotelCity]?.hotels || [];
      const realHotel = realHotelsForCity[0] || {};
      
      let nameFromItinerary = hotel.propertyName || hotel.hotelName || hotel.name;
      let fallbackName = nameFromItinerary || realHotel.name || 'Hotel';
      if (fallbackName.includes(' in ') && hotelCity) {
        fallbackName = fallbackName.replace(/in ([a-zA-Z\s]+)$/i, `in ${hotelCity}`);
      }
      const finalHotelName = fallbackName;

      const maxNameW = contentWidth - 85; 
      const hotelNameLines = splitText(finalHotelName, maxNameW);
      
      let starsY = badgeY + 18 + (hotelNameLines.length - 1) * 5 + 4;
      let gridY = starsY + 8;
      
      let finalMealPlan = tripData?.mealPlan || hotel.mealPlan;
      if (finalMealPlan) {
          const mp = finalMealPlan;
          if (mp === "EP") finalMealPlan = "Room Only";
          else if (mp === "CP") finalMealPlan = "Breakfast Included";
          else if (mp === "MAP") finalMealPlan = "Breakfast & Dinner Included";
          else if (mp === "AP") finalMealPlan = "All Meals Included";
      }
      finalMealPlan = finalMealPlan || 'Not specified';
      
      let finalRoomType = hotel.roomType;
      if (tripData?.accommodation && tripData.accommodation.length > 0) {
          finalRoomType = tripData.accommodation.join(", ");
          if (!finalRoomType.toLowerCase().includes("room")) {
              finalRoomType += " Room";
          }
      }
      finalRoomType = finalRoomType || 'Standard Room';

      const roomLines = splitText(`${tripData?.rooms?.length || 1} ${finalRoomType}`, 55);
      const mealLines = splitText(finalMealPlan, 55);
      
      const detailsMaxLines = Math.max(roomLines.length, mealLines.length);
      const contentBottomY = gridY + (detailsMaxLines * 4) + 6; // Space for pax
      
      // Dynamic card height
      const minCardHeight = 45;
      const cardHeight = Math.max(minCardHeight, contentBottomY - y);

      // Draw Card
      doc.setDrawColor(230, 230, 230);
      doc.setLineWidth(0.3);
      doc.setFillColor(255, 255, 255);
      doc.roundedRect(margin, y, contentWidth, cardHeight, 3, 3, 'FD');

      // Night Badges draw
      badgeX = margin + 5;
      
      doc.setFontSize(8);
      doc.setFont('helvetica', 'bold');
      const badgeWidth = doc.getTextWidth(badgeText) + 8;
      
      doc.setFillColor(219, 234, 254);
      doc.setDrawColor(59, 130, 246);
      doc.setLineWidth(0.3);
      doc.roundedRect(badgeX, badgeY, badgeWidth, 6, 1, 1, 'FD');
      doc.setTextColor(30, 64, 175);
      doc.text(badgeText, badgeX + 4, badgeY + 4.5);
      
      badgeX += badgeWidth + 4;
      
      doc.setTextColor(100, 100, 100);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(`at `, badgeX, badgeY + 4.5);
      
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text(hotelCity, badgeX + doc.getTextWidth(`at `) + 2, badgeY + 4.5);
      // Check-in text
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`Check-in on ${checkInStr}`, margin + 5, badgeY + 11);

      // Hotel Name is already calculated above
      doc.setTextColor(30, 64, 175);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text(hotelNameLines, margin + 5, badgeY + 18);
      
      // Stars (Using asterisk to avoid unicode rendering issues)
      doc.setTextColor(234, 179, 8); // Gold
      doc.setFontSize(12);
      
      let extractedStar = null;
      if (hotel.starRating) extractedStar = parseInt(hotel.starRating);
      if (!extractedStar && hotel.rating) extractedStar = parseInt(hotel.rating);
      if (!extractedStar && hotel.hotelCategory) {
        const match = hotel.hotelCategory.match(/\d+/);
        if (match) extractedStar = parseInt(match[0]);
      }
      if (!extractedStar && nameFromItinerary) {
         const match = nameFromItinerary.match(/(\d)(?:\s*star|\s*\*)/i);
         if (match) extractedStar = parseInt(match[1]);
      }
      if (!extractedStar && realHotel.starRating) extractedStar = parseInt(realHotel.starRating);
      
      let fallbackStar = 4;
      const tripAccMatch = tripData?.accommodation?.join(' ')?.match(/\d+/);
      if (tripAccMatch) fallbackStar = parseInt(tripAccMatch[0]);
      
      const starRating = extractedStar || fallbackStar || 4;
      const starStr = '* '.repeat(starRating).trim();
      doc.text(starStr, margin + 5, starsY + 1); 
      
      // Rooms and Meal Plan
      doc.setTextColor(150, 150, 150);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'bold');
      doc.text('ROOMS', margin + 5, gridY - 4);
      doc.text('MEAL PLAN', margin + 70, gridY - 4);
      
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(9);
      doc.setFont('helvetica', 'normal');
      doc.text(roomLines, margin + 5, gridY);
      doc.text(mealLines, margin + 70, gridY);


      doc.setTextColor(150, 150, 150);
      doc.setFontSize(8);
      doc.setFont('helvetica', 'normal');
      doc.text(`${paxBreakdown.adults} Pax`, margin + 5, gridY + roomLines.length * 4 + 1);

      // Photo
      const rawPhotoUrl = hotel.photo || hotel.image || hotel.imageUrl || realHotel.photo;
      let finalPhotoUrl = rawPhotoUrl;
      if (finalPhotoUrl && finalPhotoUrl.startsWith('//')) {
          finalPhotoUrl = 'https:' + finalPhotoUrl;
      }
      
      let imgData = null;
      if (finalPhotoUrl) {
        try {
          const loadImg = (src) => {
            return new Promise((resolve, reject) => {
              const img = new Image();
              img.crossOrigin = 'Anonymous';
              img.onload = () => resolve(img);
              img.onerror = () => reject();
              img.src = src;
            });
          };

          let img = null;
          if (finalPhotoUrl.startsWith('data:') || finalPhotoUrl.startsWith('/')) {
             try { img = await loadImg(finalPhotoUrl); } catch(e) {}
          } else {
             // Try wsrv proxy first
             try { 
                 const wsrvUrl = "https://wsrv.nl/?url=" + encodeURIComponent(finalPhotoUrl);
                 const timeoutPromise = new Promise((_, rej) => setTimeout(rej, 4000));
                 img = await Promise.race([loadImg(wsrvUrl), timeoutPromise]); 
             } catch(e) {}
             
             // If failed, try corsproxy.io
             if (!img) {
                 try {
                     const corsUrl = "https://corsproxy.io/?" + encodeURIComponent(finalPhotoUrl);
                     const timeoutPromise = new Promise((_, rej) => setTimeout(rej, 4000));
                     img = await Promise.race([loadImg(corsUrl), timeoutPromise]);
                 } catch(e) {}
             }
             
             // If still failed, try direct url as last resort
             if (!img) {
                 try {
                     const timeoutPromise = new Promise((_, rej) => setTimeout(rej, 4000));
                     img = await Promise.race([loadImg(finalPhotoUrl), timeoutPromise]);
                 } catch(e) {}
             }
          }
          
          if (img && img.width > 0 && img.height > 0) {
            const canvas = document.createElement('canvas');
            canvas.width = img.width;
            canvas.height = img.height;
            const ctx = canvas.getContext('2d');
            ctx.drawImage(img, 0, 0);
            try {
                imgData = canvas.toDataURL('image/jpeg', 0.8);
            } catch(e) {
                Sentry.withScope((scope) => {
                  scope.setTag("feature", "media");
                  scope.setTag("app", "web");
                  scope.setTag("error_source", "browser");
                  Sentry.captureException(new MediaError(e instanceof Error ? e.message : String(e)));
                });
                console.warn("Canvas tainted by CORS", e);
            }
          }
        } catch (e) {
          Sentry.withScope((scope) => {
            scope.setTag("feature", "media");
            scope.setTag("app", "web");
            scope.setTag("error_source", "browser");
            Sentry.captureException(new MediaError(e instanceof Error ? e.message : String(e)));
          });
          console.error("Failed to load hotel image", e);
        }
      }

      // If no valid hotel image found, load a generic placeholder instead of just text
      if (!imgData) {
         try {
             // A nice generic hotel placeholder from Unsplash
             const placeholderUrl = "https://images.unsplash.com/photo-1566073771259-6a8506099945?w=400&q=80";
             const img = new Image();
             img.crossOrigin = 'Anonymous';
             img.src = "https://wsrv.nl/?url=" + encodeURIComponent(placeholderUrl);
             await new Promise((resolve) => {
                img.onload = () => resolve();
                img.onerror = () => resolve();
                setTimeout(resolve, 2000);
             });
             if (img.width > 0) {
                const canvas = document.createElement('canvas');
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0);
                imgData = canvas.toDataURL('image/jpeg', 0.8);
             }
         } catch(e) {}
      }

      if (imgData) {
        try {
          doc.addImage(imgData, 'JPEG', margin + contentWidth - 65, y + 5, 60, cardHeight - 10);
        } catch (e) {
          Sentry.withScope((scope) => {
            scope.setTag("feature", "media");
            scope.setTag("app", "web");
            scope.setTag("error_source", "browser");
            Sentry.captureException(new MediaError(e instanceof Error ? e.message : String(e)));
          });
          // Fallback if addImage fails
          doc.setFillColor(243, 244, 246);
          doc.roundedRect(margin + contentWidth - 65, y + 5, 60, cardHeight - 10, 2, 2, 'F');
          doc.setTextColor(200, 200, 200);
          doc.text('Image Load Error', margin + contentWidth - 35, y + (cardHeight/2), { align: 'center' });
        }
      } else {
        doc.setFillColor(243, 244, 246);
        doc.roundedRect(margin + contentWidth - 65, y + 5, 60, cardHeight - 10, 2, 2, 'F');
        doc.setTextColor(200, 200, 200);
        doc.text('Image Not Available', margin + contentWidth - 35, y + (cardHeight/2), { align: 'center' });
      }

      y += cardHeight + 8;
      currentNightIdx += nightsNum;
    }
  }


  // --- Page 4: Transportation and Activities ---
  if (dayWiseItinerary && dayWiseItinerary.length > 0) {
    doc.addPage();
    y = 20;

    // Main Header
    doc.setFillColor(191, 219, 254);
    doc.rect(margin, y, contentWidth, 12, 'F');
    doc.setFillColor(37, 99, 235);
    doc.rect(margin, y, 4, 12, 'F');
    doc.setTextColor(37, 99, 235);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Transportation and Activities', margin + 10, y + 8);
    y += 18;

    const colDayWidth = 35;
    const colServiceWidth = contentWidth - colDayWidth;
    const subColLeftWidth = colServiceWidth * 0.55;
    const subColRightWidth = colServiceWidth * 0.45;
    const dayX = margin;
    const serviceX = margin + colDayWidth;
    const rightSubX = serviceX + subColLeftWidth;

    const drawTableHeader = () => {
      doc.setDrawColor(230, 230, 230);
      doc.setFillColor(255, 255, 255);
      doc.rect(margin, y, contentWidth, 10, 'FD');
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Day', dayX + 3, y + 7);
      doc.text('Service', serviceX + 3, y + 7);
      doc.line(serviceX, y, serviceX, y + 10);
      y += 10;
    };

    drawTableHeader();

    const getOrdinal = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };
    
    const paxString = (() => {
      let pax = [];
      if (tripData?.adults) pax.push(`${tripData.adults}Ad.`);
      if (tripData?.children) pax.push(`${tripData.children}Ch.`);
      if (tripData?.infants) pax.push(`${tripData.infants}Inf.`);
      return pax.join(' + ');
    })();

    for (let index = 0; index < dayWiseItinerary.length; index++) {
      const day = dayWiseItinerary[index];
      const dayNumStr = getOrdinal(day.dayNumber || index + 1);
      const dayDateStr = day.date ? formatSafeDate(day.date, { weekday: 'short', day: 'numeric', month: 'short' }) : '';
      
      const activities = day.activities || [];
      if (activities.length === 0) {
        activities.push({ title: day.title || 'Leisure Day', type: 'Leisure', description: day.overview || 'Enjoy a day at leisure.', category: 'leisure' });
      }

      for (let actIdx = 0; actIdx < activities.length; actIdx++) {
        const activity = activities[actIdx];
        const isFirstActivityOfDay = actIdx === 0;

        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        const titleLines = splitText(activity.title || activity.description || 'Activity', subColLeftWidth - 6);
        
        const rightLines = [];
        let actType = formatCategoryName(activity.type || activity.category || 'Sightseeing');
        if (activity.title && activity.title.toLowerCase().includes('flight')) {
           actType = 'Flight';
        }
        rightLines.push(actType);
        
        if (paxString) rightLines.push(paxString);
        
        if (activity.time || activity.duration) {
          let timeLine = '';
          if (activity.time) timeLine += `Starts: ${activity.time}`;
          if (activity.duration) {
            timeLine += timeLine ? ` (Duration: ${activity.duration})` : `Duration: ${activity.duration}`;
          }
          const timeSplits = splitText(timeLine, subColRightWidth - 6);
          rightLines.push(...timeSplits);
        }

        const maxLines = Math.max(titleLines.length, rightLines.length);
        let rowHeight = Math.max(maxLines * 5 + 6, isFirstActivityOfDay ? 15 : 10);
        
        if (y + rowHeight > pageHeight - 25) {
          doc.addPage();
          y = 20;
          drawTableHeader();
        }

        // Draw Row Border
        doc.setDrawColor(230, 230, 230);
        doc.setFillColor(255, 255, 255);
        doc.rect(margin, y, contentWidth, rowHeight, 'FD');
        doc.line(serviceX, y, serviceX, y + rowHeight);
        doc.line(rightSubX, y, rightSubX, y + rowHeight);

        // Print Day text if first
        if (isFirstActivityOfDay) {
          doc.setTextColor(30, 30, 30);
          doc.setFontSize(10);
          doc.setFont('helvetica', 'bold');
          doc.text(`${dayNumStr} Day`, dayX + 3, y + 6);
          doc.setFontSize(8);
          doc.setFont('helvetica', 'normal');
          doc.setTextColor(100, 100, 100);
          doc.text(dayDateStr, dayX + 3, y + 11);
        }

        // Print Title
        doc.setTextColor(50, 50, 50);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        doc.text(titleLines, serviceX + 3, y + 6);

        // Print Right Col
        let rightY = y + 6;
        doc.setTextColor(100, 100, 100);
        rightLines.forEach((line) => {
           doc.text(line, rightSubX + 3, rightY);
           rightY += 5;
        });

        y += rowHeight;
      }
    }
  }

  // --- Page 5: Day Wise Detailed Itinerary ---
  if (dayWiseItinerary && dayWiseItinerary.length > 0) {
    doc.addPage();
    y = 20;

    // Header
    doc.setFillColor(191, 219, 254);
    doc.rect(margin, y, contentWidth, 12, 'F');
    doc.setFillColor(37, 99, 235);
    doc.rect(margin, y, 4, 12, 'F');
    doc.setTextColor(37, 99, 235);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Day Wise Detailed Itinerary', margin + 10, y + 8);
    y += 20;

    const getOrdinal = (n) => {
      const s = ["th", "st", "nd", "rd"];
      const v = n % 100;
      return n + (s[(v - 20) % 10] || s[v] || s[0]);
    };

    let imageService = null;
    try {
      imageService = await import('../../services/imageService.js');
    } catch(e) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "media");
        scope.setTag("app", "web");
        scope.setTag("error_source", "browser");
        Sentry.captureException(new MediaError(e instanceof Error ? e.message : String(e)));
      });
      console.warn("Could not load imageService in pdfGenerator", e);
    }

    for (let index = 0; index < dayWiseItinerary.length; index++) {
      const day = dayWiseItinerary[index];
      const dayNumStr = getOrdinal(day.dayNumber || index + 1);
      const dayDateStr = day.date ? formatSafeDate(day.date, { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' }) : '';
      
      const activities = day.activities || [];
      if (activities.length === 0) continue;

      for (let actIdx = 0; actIdx < activities.length; actIdx++) {
        const activity = activities[actIdx];
        const isFirstActivityOfDay = actIdx === 0;

        checkPageBreak(50);

        // Badge
        doc.setFillColor(219, 234, 254);
        doc.setDrawColor(59, 130, 246);
        doc.setLineWidth(0.3);
        doc.roundedRect(margin, y, 14, 14, 1, 1, 'FD');
        doc.setTextColor(30, 64, 175);
        doc.setFont('helvetica', 'bold');
        
        if (isFirstActivityOfDay) {
          doc.setFontSize(9);
          doc.text(dayNumStr, margin + 7, y + 6, { align: 'center' });
          doc.setFontSize(6);
          doc.text('Day', margin + 7, y + 11, { align: 'center' });
        } else {
          doc.setFontSize(16);
          doc.text('+', margin + 7, y + 9.5, { align: 'center' });
        }

        // Date
        doc.setTextColor(30, 30, 30);
        doc.setFontSize(8);
        doc.setFont('helvetica', 'bold');
        doc.text(dayDateStr, margin + 20, y + 4);

        // Title
        doc.setTextColor(30, 64, 175);
        doc.setFontSize(12);
        doc.setFont('helvetica', 'bold');
        const titleLines = splitText(activity.title || activity.description || 'Activity', contentWidth - 25);
        doc.text(titleLines, margin + 20, y + 10);

        let currentY = y + 10 + (titleLines.length - 1) * 5;

        // Duration / Tips
        let durationText = activity.duration ? String(activity.duration) : '';
        if (durationText) {
          if (!durationText.toLowerCase().includes('time')) {
             durationText = 'Est. Travel Time: ' + durationText;
          }
          currentY += 5;
          doc.setTextColor(150, 150, 150);
          doc.setFontSize(7);
          doc.setFont('helvetica', 'normal');
          doc.text(durationText, margin + 20, currentY);
        }

        y = Math.max(y + 16, currentY + 4);
        
        // Image
        try {
          const skipImage = ['transport', 'accommodation', 'leisure'].includes(String(activity.category || '').toLowerCase()) || 
                            activity.isArrival || activity.isDeparture || activity.isTransfer || activity.isInterCity || 
                            /cab|transfer|checkout|check in|airport|arrival/i.test(activity.title || '');
                            
          if (!skipImage && imageService && imageService.getActivityImages) {
             const images = await imageService.getActivityImages(activity, tripData?.destinations?.[0]?.name || 'India', 1, actIdx);
             if (images && images.length > 0) {
               const photoUrl = images[0].url || images[0].thumbnail;
               if (photoUrl) {
                 checkPageBreak(70);
                 let imgData = null;
                 try {
                   const img = new Image();
                   img.crossOrigin = 'Anonymous';
                   img.src = "https://wsrv.nl/?url=" + encodeURIComponent(photoUrl);
                   await new Promise((resolve) => {
                     img.onload = () => resolve();
                     img.onerror = () => resolve();
                     setTimeout(resolve, 3000);
                   });
                   if (img.width > 0 && img.height > 0) {
                     const canvas = document.createElement('canvas');
                     canvas.width = img.width;
                     canvas.height = img.height;
                     const ctx = canvas.getContext('2d');
                     ctx.drawImage(img, 0, 0);
                     imgData = canvas.toDataURL('image/jpeg', 0.8);
                   }
                 } catch (e) {
                   Sentry.withScope((scope) => {
                     scope.setTag("feature", "media");
                     scope.setTag("app", "web");
                     scope.setTag("error_source", "browser");
                     Sentry.captureException(new MediaError(e instanceof Error ? e.message : String(e)));
                   });
                   console.error("Failed to process activity image", e);
                 }
                 
                 if (imgData) {
                   try {
                     doc.addImage(imgData, 'JPEG', margin + 20, y, contentWidth - 20, 60);
                     y += 65;
                   } catch (e) {
                     Sentry.withScope((scope) => {
                       scope.setTag("feature", "media");
                       scope.setTag("app", "web");
                       scope.setTag("error_source", "browser");
                       Sentry.captureException(new MediaError(e instanceof Error ? e.message : String(e)));
                     });
                     console.error("Failed to draw activity image to pdf", e);
                   }
                 }
               }
             }
          }
        } catch (imgError) {
          Sentry.withScope((scope) => {
            scope.setTag("feature", "media");
            scope.setTag("app", "web");
            scope.setTag("error_source", "browser");
            Sentry.captureException(new MediaError(imgError instanceof Error ? imgError.message : String(imgError)));
          });
          console.warn("Failed to load activity image for PDF:", imgError);
        }

        // Description
        let descText = activity.description || activity.overview || '';
        if (!descText) {
          descText = `Experience the best of ${activity.title || 'this activity'}. Enjoy a wonderful time exploring the sights, sounds, and local culture. This is a great opportunity to make unforgettable memories during your trip.`;
        }
        
        if (activity.tips) {
          descText += `\n\nTip: ${activity.tips}`;
        } else if (!activity.isTransfer) {
          descText += `\n\nTip: Don't forget to carry your camera and some local currency for small purchases or entry tickets if applicable.`;
        }
        
        if (descText) {
          doc.setTextColor(50, 50, 50);
          doc.setFontSize(9);
          doc.setFont('helvetica', 'normal');
          const descLines = splitText(descText, contentWidth - 20);
          checkPageBreak(descLines.length * 5 + 10);
          doc.text(descLines, margin + 20, y);
          y += descLines.length * 5 + 10;
        } else {
          y += 5;
        }
      }
    }
  }



  // --- Page 5: Inclusions and Exclusions ---
  if ((inclusions && inclusions.length > 0) || (exclusions && exclusions.length > 0)) {
    doc.addPage();
    y = 20;

    // Header
    doc.setFillColor(191, 219, 254);
    doc.roundedRect(margin, y, contentWidth, 12, 2, 2, 'F');
    doc.setFillColor(37, 99, 235);
    // Draw left square border to cover the rounded left corners
    doc.rect(margin, y, 4, 12, 'F');
    doc.setTextColor(37, 99, 235);
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Inclusions/Exclusions', margin + 10, y + 8);
    y += 22;

    if (inclusions && inclusions.length > 0) {
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Inclusions', margin, y + 5);
      // underline
      doc.setDrawColor(34, 197, 94); // green-500
      doc.setLineWidth(0.6);
      doc.line(margin, y + 6.5, margin + doc.getTextWidth('Inclusions') + 2, y + 6.5);
      y += 12;

      inclusions.forEach(inc => {
        checkPageBreak(15);
        doc.setFillColor(34, 197, 94); // green-500
        doc.circle(margin + 2, y + 3, 1, 'F');
        doc.setTextColor(50, 50, 50);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const incText = typeof inc === 'string' ? inc : (inc.name || inc.title || '');
        const incLines = splitText(incText, contentWidth - 10);
        checkPageBreak(incLines.length * 5 + 5);
        doc.text(incLines, margin + 6, y + 4);
        y += incLines.length * 5 + 4;
      });
      y += 8;
    }

    if (exclusions && exclusions.length > 0) {
      checkPageBreak(30);
      doc.setTextColor(30, 30, 30);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Exclusions', margin, y + 5);
      // underline
      doc.setDrawColor(220, 38, 38); // red-600
      doc.setLineWidth(0.6);
      doc.line(margin, y + 6.5, margin + doc.getTextWidth('Exclusions') + 2, y + 6.5);
      y += 12;

      exclusions.forEach(exc => {
        checkPageBreak(15);
        doc.setFillColor(239, 68, 68); // red-500
        doc.circle(margin + 2, y + 3, 1, 'F');
        doc.setTextColor(50, 50, 50);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const excText = typeof exc === 'string' ? exc : (exc.name || exc.title || '');
        const excLines = splitText(excText, contentWidth - 10);
        checkPageBreak(excLines.length * 5 + 5);
        doc.text(excLines, margin + 6, y + 4);
        y += excLines.length * 5 + 4;
      });
      y += 12;
    }

    // Anything not mentioned...
    checkPageBreak(15);
    doc.setDrawColor(220, 38, 38);
    doc.setLineWidth(1.5); // Thicker line
    doc.line(margin, y, margin, y + 5);
    doc.setTextColor(146, 64, 14); // amber-800 or brown
    doc.setFontSize(9);
    doc.setFont('helvetica', 'bold');
    doc.text('Anything not mentioned in the inclusions is excluded.', margin + 4, y + 4);
    y += 15;
  }

  if (packingChecklist && Object.keys(packingChecklist).length > 0) {
    doc.addPage();
    y = 15;

    doc.setFillColor(...teal);
    doc.roundedRect(margin, y, contentWidth, 12, 3, 3, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Packing Checklist', margin + 6, y + 8);
    y += 18;

    const colWidth = (contentWidth - 10) / 2;
    let leftY = y;
    let rightY = y;
    let useLeft = true;

    const categories = Object.entries(packingChecklist);

    categories.forEach(([category, items]) => {
      if (!Array.isArray(items) || items.length === 0) return;

      const catHeight = 10 + items.length * 5;

      const currentY = useLeft ? leftY : rightY;
      if (currentY + catHeight > pageHeight - 25) {
        if (useLeft && rightY + catHeight <= pageHeight - 25) {
          useLeft = false;
        } else {
          doc.addPage();
          y = 20;
          leftY = 20;
          rightY = 20;
          useLeft = true;
        }
      }

      const catY = useLeft ? leftY : rightY;
      const catX = useLeft ? margin : margin + colWidth + 10;

      doc.setFillColor(...tealLight);
      doc.roundedRect(catX, catY, colWidth, 8, 2, 2, 'F');
      doc.setTextColor(...teal);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'bold');
      doc.text(formatCategoryName(category), catX + 4, catY + 5.5);

      let itemY = catY + 12;

      items.forEach((item, idx) => {
        const itemText = typeof item === 'object' ? (item.name || item.item || '') : String(item);
        if (!itemText) return;

        const checkKey = `${category}-${idx}`;
        const isChecked = checkedPackingItems[checkKey] || false;

        doc.setDrawColor(...gray);
        doc.setLineWidth(0.4);
        doc.rect(catX + 3, itemY - 2.5, 3.5, 3.5, 'S');

        if (isChecked) {
          doc.setDrawColor(...teal);
          doc.setLineWidth(0.6);
          doc.line(catX + 3.5, itemY - 0.5, catX + 4.5, itemY + 0.5);
          doc.line(catX + 4.5, itemY + 0.5, catX + 6, itemY - 1.5);
        }

        if (isChecked) {
          doc.setTextColor(...gray);
        } else {
          doc.setTextColor(...darkGray);
        }
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        const itemLines = splitText(itemText, colWidth - 15);
        doc.text(itemLines[0] || '', catX + 10, itemY);

        if (isChecked) {
          const textWidth = doc.getTextWidth(itemLines[0] || '');
          doc.setDrawColor(...gray);
          doc.setLineWidth(0.3);
          doc.line(catX + 10, itemY - 1, catX + 10 + textWidth, itemY - 1);
        }

        itemY += 5;
      });

      itemY += 5;

      if (useLeft) {
        leftY = itemY;
      } else {
        rightY = itemY;
      }

      useLeft = !useLeft;
    });
  }

  if (importantNotes && importantNotes.length > 0) {
    doc.addPage();
    y = 15;

    doc.setFillColor(...amber);
    doc.roundedRect(margin, y, contentWidth, 12, 3, 3, 'F');
    doc.setTextColor(...white);
    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.text('Important Information', margin + 6, y + 8);
    y += 18;

    doc.setFillColor(...amberLight);
    doc.roundedRect(margin, y, contentWidth, pageHeight - y - 30, 3, 3, 'F');

    y += 8;

    importantNotes.forEach((note) => {
      if (y > pageHeight - 35) {
        doc.addPage();
        y = 20;
        doc.setFillColor(...amberLight);
        doc.roundedRect(margin, y - 5, contentWidth, pageHeight - y - 25, 3, 3, 'F');
      }

      doc.setFillColor(...amber);
      doc.circle(margin + 6, y, 1.5, 'F');

      doc.setTextColor(120, 53, 15);
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      const noteLines = splitText(note, contentWidth - 20);
      doc.text(noteLines, margin + 12, y + 1);
      y += noteLines.length * 5 + 5;
    });
  }

  
  // --- Page 6: Terms and Conditions ---
  doc.addPage();
  y = 20;

  // Header
  doc.setFillColor(191, 219, 254);
  doc.roundedRect(margin, y, contentWidth, 12, 2, 2, 'F');
  doc.setFillColor(37, 99, 235);
  doc.rect(margin, y, 4, 12, 'F');
  doc.setTextColor(37, 99, 235);
  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Terms and Conditions', margin + 10, y + 8);
  y += 22;

  const terms = [
    { num: "1.", text: "All prices for hotels, private transfers, and activities are subject to change based on availability, demand and dynamic pricing. Hotel room availability, train seats, flight seats and activities are on a first-come, first-served basis, rates may fluctuate accordingly.", boldPrefixLength: 119 },
    { num: "2.", text: "We recommend confirming your booking early to secure the best rates and availability." },
    { num: "3.", text: "Infant (below 3 years) are free in hotels and private transfers." },
    { num: "4.", text: "These package prices are valid only till today night." },
    { num: "5.", text: "Kindly make 50 percent of the payment advance by today to block this package at these rates." },
    { num: "6.", text: "Please check all the activity voucher timings and hotel name and address mentioned in the actual vouchers only as the source of truth." },
    { num: "7.", text: "This package is 100 percent non-refundable." },
    { num: "8.", text: "All activities are subject to availability and may change based on weather conditions or last-minute operational changes." },
    { num: "9.", text: "Strict timings to be followed - 10 AM to 9 PM. Beyond 9 PM, driver will take night driving charges." },
    { num: "10.", text: "5% GST and 2% / 20% TCS will be added on the total package amount." },
    { num: "11.", text: "As per Government rules, clients must submit a self-declaration form confirming their total overseas travel spend for the financial year.", boldSub: "clients must submit a self-declaration form confirming their total overseas travel spend for the financial year." },
    { num: "", text: "• If the total package including GST is below 10 lakhs, TCS is 2%.", isSub: true },
    { num: "", text: "• If the total package including GST is above 10 lakhs, TCS is 20%.", isSub: true },
    { num: "12.", text: "TCS can be reclaimed at the time of ITR filing." },
    { num: "13.", text: "All matters are subject to Bengaluru jurisdiction." },
    { num: "14.", text: "E & OE (Errors and Omissions Excepted)." }
  ];

  doc.setTextColor(50, 50, 50);
  doc.setFontSize(9);

  terms.forEach(term => {
    checkPageBreak(15);
    
    let xOffset = margin;
    if (term.isSub) {
      xOffset = margin + 10;
    } else {
      doc.setFont('helvetica', 'normal');
      doc.text(term.num, margin, y);
      xOffset = margin + 5;
    }

    if (term.num === "1.") {
      doc.setFont('helvetica', 'bold');
      doc.text('All prices for hotels, private transfers, and activities are subject to change based on', xOffset, y);
      y += 5;
      const boldText2 = 'availability, demand and dynamic pricing.';
      doc.text(boldText2, xOffset, y);
      const boldWidth2 = doc.getTextWidth(boldText2);
      doc.setFont('helvetica', 'normal');
      doc.text(' Hotel room availability, train seats, flight seats and', xOffset + boldWidth2, y);
      y += 5;
      doc.text('activities are on a first-come, first-served basis, rates may fluctuate accordingly.', xOffset, y);
      y += 6.5;
    } else if (term.num === "11.") {
      doc.setFont('helvetica', 'normal');
      const normalText = 'As per Government rules, ';
      doc.text(normalText, xOffset, y);
      const normalWidth = doc.getTextWidth(normalText);
      doc.setFont('helvetica', 'bold');
      doc.text('clients must submit a self-declaration form confirming their total', xOffset + normalWidth, y);
      y += 5;
      doc.text('overseas travel spend for the financial year.', xOffset, y);
      y += 6.5;
    } else if (term.isSub) {
      doc.setFont('helvetica', 'normal');
      if (term.text.includes('below')) {
        doc.text('• If the total package including GST is ', xOffset, y);
        let currX = xOffset + doc.getTextWidth('• If the total package including GST is ');
        doc.setFont('helvetica', 'bold');
        doc.text('below 10 lakhs', currX, y);
        currX += doc.getTextWidth('below 10 lakhs');
        doc.setFont('helvetica', 'normal');
        doc.text(', TCS is ', currX, y);
        currX += doc.getTextWidth(', TCS is ');
        doc.setFont('helvetica', 'bold');
        doc.text('2%.', currX, y);
      } else {
        doc.text('• If the total package including GST is ', xOffset, y);
        let currX = xOffset + doc.getTextWidth('• If the total package including GST is ');
        doc.setFont('helvetica', 'bold');
        doc.text('above 10 lakhs', currX, y);
        currX += doc.getTextWidth('above 10 lakhs');
        doc.setFont('helvetica', 'normal');
        doc.text(', TCS is ', currX, y);
        currX += doc.getTextWidth(', TCS is ');
        doc.setFont('helvetica', 'bold');
        doc.text('20%.', currX, y);
      }
      y += 6.5;
    } else {
      doc.setFont('helvetica', 'normal');
      const lines = splitText(term.text, contentWidth - 10);
      doc.text(lines, xOffset, y);
      y += lines.length * 5 + 1.5;
    }
  });

  // Footer Signature Area
  const signatureHeight = 45;
  if (y + signatureHeight > pageHeight - 25) {
    doc.addPage();
    y = 20;
  } else {
    y = pageHeight - signatureHeight - 20;
  }

  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  
  y += 10;
  
  // Left side: Logo + Brand Name
  try {
    const footerLogoBase64 = await loadImageAsBase64('/RoamMyWay.png');
    doc.addImage(footerLogoBase64, 'PNG', margin, y, 30, 10);
  } catch(e) {}
  
  doc.setTextColor(30, 30, 30);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text('RoamMyWay', margin, y + 15);

  // Right side: Holiday Expert
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('Holiday Expert', pageWidth - margin, y + 2, { align: 'right' });
  
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.text('RoamMyWay', pageWidth - margin, y + 8, { align: 'right' });
  
  doc.setTextColor(37, 99, 235); // blue-600
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.text('+91-8949491119', pageWidth - margin, y + 13, { align: 'right' });

  // Bottom address line
  y += 22;
  doc.setDrawColor(230, 230, 230);
  doc.setLineWidth(0.5);
  doc.line(margin, y, pageWidth - margin, y);
  
  y += 5;
  doc.setTextColor(100, 100, 100);
  doc.setFontSize(7);
  doc.text('209 Manidhari Enclave, Chamrajpet Police Station, Bangalore, Karnataka, India - 560018', margin, y);

    // --- Page 7: Poster Page ---
  doc.addPage();
  if (coverBase64) {
    try {
      doc.addImage(coverBase64, 'JPEG', 0, 0, pageWidth, pageHeight);
    } catch(e) {}
  }
  
  // Blue overlay
  doc.setGState(new doc.GState({opacity: 0.85}));
  doc.setFillColor(14, 165, 233); // A nice vibrant sky blue
  doc.rect(0, 0, pageWidth, pageHeight, 'F');
  doc.setGState(new doc.GState({opacity: 1.0}));

  // Logo at top center
  try {
    const posterLogoBase64 = await loadImageAsBase64('/RoamMyWay.png');
    doc.setFillColor(255, 255, 255);
    doc.rect((pageWidth - 60) / 2, 10, 60, 25, 'F'); 
    doc.addImage(posterLogoBase64, 'PNG', ((pageWidth - 56) / 2), 12, 56, 21);
  } catch(e) {}

  // "YOUR [DESTINATION] ESCAPE AWAITS!"
  let rawDest = tripData?.destinations?.[0]?.name || tripSummary?.destination || destTitle || "DREAM";
  if (typeof rawDest === 'string') {
      if (rawDest.includes(',')) rawDest = rawDest.split(',')[0];
      if (rawDest.includes(' & ')) rawDest = rawDest.split(' & ')[0];
      if (rawDest.includes(' and ')) rawDest = rawDest.split(' and ')[0];
  }
  const destStr = (rawDest || "DREAM").toUpperCase().replace(/[^A-Z\s]/g, '').trim();
  
  doc.setTextColor(255, 255, 255);
  doc.setFont('helvetica', 'bold');
  
  doc.setFontSize(50);
  let titleLines = doc.splitTextToSize(`YOUR ${destStr}`, pageWidth - 20);
  let titleY = pageHeight / 2 - 40; // Center it vertically a bit more
  doc.text(titleLines, pageWidth / 2, titleY, { align: 'center' });
  
  doc.setFontSize(55);
  let subtitleLines = doc.splitTextToSize("ESCAPE AWAITS!", pageWidth - 20);
  let subY = titleY + (titleLines.length * 18) + 5;
  doc.text(subtitleLines, pageWidth / 2, subY, { align: 'center' });

  // Add decorative lines
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.8);
  const lineY = subY + (subtitleLines.length * 18);
  doc.line(40, lineY, pageWidth - 40, lineY);
  doc.line(50, lineY + 6, pageWidth - 50, lineY + 6);

  // Footer section
  const footerH = 15;
  const footerW = pageWidth - 20;
  const footerY = pageHeight - 20;
  
  // Book Now button
  doc.setDrawColor(255, 255, 255);
  doc.setLineWidth(0.5);
  doc.setFillColor(255, 255, 255);
  doc.setGState(new doc.GState({opacity: 0.25}));
  doc.roundedRect((pageWidth - 40) / 2, footerY - 20, 40, 10, 3, 3, 'F');
  doc.setGState(new doc.GState({opacity: 1.0}));
  doc.roundedRect((pageWidth - 40) / 2, footerY - 20, 40, 10, 3, 3, 'S');
  
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(12);
  doc.text("Book Now", pageWidth / 2, footerY - 13, { align: 'center' });

  doc.setFillColor(243, 244, 246);
  doc.roundedRect(10, footerY, footerW, footerH, 5, 5, 'F');
  
  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.text("Vidhan Mehta: +91 63762 00925", 15, footerY + 6.5);
  doc.text("Manan Mehta: +91 63759 13360", 15, footerY + 11.5);
  
  doc.setFontSize(12);
  doc.text("www.roammyway.in", pageWidth - 15, footerY + 9, { align: 'right' });

  const totalPages = doc.internal.getNumberOfPages();

  // Start from page 2 to avoid drawing on the cover page and skip the last poster page
  for (let i = 2; i < totalPages; i++) {
    doc.setPage(i);

    doc.setDrawColor(...teal);
    doc.setLineWidth(0.4);
    doc.line(margin, pageHeight - 15, pageWidth - margin, pageHeight - 15);

    doc.setTextColor(...gray);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const footerDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
    doc.text(`Generated by RoamMyWay  |  ${footerDate}`, margin, pageHeight - 9);

    doc.setTextColor(...teal);
    doc.setFont('helvetica', 'bold');
    doc.text(`Page ${i} of ${totalPages}`, pageWidth - margin, pageHeight - 9, { align: 'right' });
  }

  const fileName = `${(tripSummary?.destination || 'Trip').replace(/[^a-zA-Z0-9]/g, '_')}_Itinerary.pdf`;
  doc.save(fileName);
};
